import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { UpdatePostRequest } from "../../entry/server/post.ts";
import { postRepo } from "../../repo/post-repo.ts";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { AppContext } from "../../context/app-context.tsx";
import { ASSETS_BASE_URL } from "../../utils/constants.ts";
import PostFormFields, { isPostFormValid } from "./post-form-fields.tsx";

function assetUrl(path: string) {
  if (!path) return "";
  return path.includes("http") ? path : `${ASSETS_BASE_URL}${path}`;
}

export default function EditPost() {
  const navigate = useNavigate();
  const { id } = useParams();
  const postId = Number(id);
  const { setMessage } = useContext(AppContext);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [currentThumbnail, setCurrentThumbnail] = useState("");
  const [thumbnailError, setThumbnailError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const thumbnailPreviewUrl = useMemo(() => {
    if (thumbnailFile) return URL.createObjectURL(thumbnailFile);
    return currentThumbnail;
  }, [thumbnailFile, currentThumbnail]);

  useEffect(() => {
    return () => {
      if (thumbnailFile && thumbnailPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(thumbnailPreviewUrl);
      }
    };
  }, [thumbnailFile, thumbnailPreviewUrl]);

  useEffect(() => {
    if (!postId) {
      setMessage({ type: "error", message: "شناسه پست معتبر نیست." });
      navigate("/admin/posts");
      return;
    }

    const fetchPost = async () => {
      setIsLoading(true);
      try {
        const response = await postRepo.getPost(postId);
        if (!response.status || !response.data) {
          setMessage({ type: "error", message: response.message || "دریافت اطلاعات پست ناموفق بود." });
          navigate("/admin/posts");
          return;
        }

        const data = response.data;
        setTitle(data.title);
        setContent(data.content ?? "");
        setIsPublished(Boolean(data.is_published));
        setCurrentThumbnail(assetUrl(data.thumbnail));
        setThumbnailFile(null);
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
        navigate("/admin/posts");
      } finally {
        setIsLoading(false);
      }
    };

    void fetchPost();
  }, [postId, navigate, setMessage]);

  const onThumbnailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setThumbnailError("فقط فایل تصویری مجاز است.");
      return;
    }

    setThumbnailError("");
    setThumbnailFile(file);
  };

  const formState = {
    title,
    content,
    isPublished,
    thumbnailFile,
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isPostFormValid(formState, "edit") || !postId) {
      setMessage({ type: "error", message: "لطفاً همه فیلدهای الزامی را کامل کنید." });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: UpdatePostRequest = {
        title: title.trim(),
        content: content,
        is_published: isPublished ? "true" : "false",
      };
      if (thumbnailFile) {
        payload.thumbnail = thumbnailFile;
      }

      const response = await postRepo.updatePost(postId, payload);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "ویرایش پست ناموفق بود." });
        return;
      }

      setMessage({ type: "success", message: "پست با موفقیت ویرایش شد." });
      navigate("/admin/posts");
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDelete = async () => {
    if (!postId || !window.confirm("آیا مطمئن هستید که می‌خواهید این پست را حذف کنید؟")) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await postRepo.deletePost(postId);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "حذف پست ناموفق بود." });
        return;
      }
      setMessage({ type: "success", message: "پست با موفقیت حذف شد." });
      navigate("/admin/posts");
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <section className="space-y-4">
        <AdminPageTitle title="ویرایش پست" />
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت اطلاعات پست...
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <AdminPageTitle title="ویرایش پست" />
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
          <PostFormFields
            mode="edit"
            title={title}
            content={content}
            isPublished={isPublished}
            thumbnailFile={thumbnailFile}
            thumbnailPreviewUrl={thumbnailPreviewUrl}
            thumbnailError={thumbnailError}
            onTitleChange={setTitle}
            onContentChange={setContent}
            onPublishedChange={setIsPublished}
            onThumbnailChange={onThumbnailChange}
          />
          <div className="flex flex-wrap items-center gap-3 md:col-span-2">
            <button
              type="submit"
              disabled={!isPostFormValid(formState, "edit") || isSubmitting}
              className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "در حال ذخیره…" : "ذخیره تغییرات"}
            </button>
            <Link
              to="/admin/posts"
              className="inline-flex rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300"
            >
              انصراف
            </Link>
            <button
              type="button"
              onClick={onDelete}
              disabled={isDeleting}
              className="rounded-lg border border-red-700 px-4 py-2 text-sm text-red-200 transition hover:border-red-400 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isDeleting ? "در حال حذف…" : "حذف پست"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
